import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import GroupCalendar from '../components/GroupCalendar';
import GroupBookingForm from '../components/GroupBookingForm';
import GroupBookingComplete from '../components/GroupBookingComplete';
import { useCourses } from '../contexts/CourseContext';
import { getGroupAvailableDates } from '../services/groupBookingService';
import { DateStatus, GroupBookingResult, TimeSlot, SlotStatus } from '../types/groupBooking';

const GroupBookingCalendar: React.FC = () => {
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const { courses } = useCourses();

  const program = courses.find(c => c.id === programId);

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dateStatuses, setDateStatuses] = useState<Record<string, DateStatus>>({});
  const [slotsByDate, setSlotsByDate] = useState<Record<string, Record<TimeSlot, SlotStatus>>>({});
  const [useTimeSlots, setUseTimeSlots] = useState(false);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [loading, setLoading] = useState(true);
  const [completedBooking, setCompletedBooking] = useState<GroupBookingResult | null>(null);

  const fetchDates = useCallback(async () => {
    if (!programId) return;
    setLoading(true);

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;

    const result = await getGroupAvailableDates(programId, year, month);

    if (result.success && result.data) {
      const statusMap: Record<string, DateStatus> = {};
      const slotMap: Record<string, Record<TimeSlot, SlotStatus>> = {};
      for (const d of result.data.dates) {
        statusMap[d.date] = d.status as DateStatus;
        if (d.slots) slotMap[d.date] = d.slots;
      }
      setDateStatuses(statusMap);
      setSlotsByDate(slotMap);
      setUseTimeSlots(result.data.use_time_slots === true);
    }

    setLoading(false);
  }, [programId, currentMonth]);

  useEffect(() => {
    fetchDates();
  }, [fetchDates]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleDateToggle = (date: string) => {
    if (useTimeSlots) {
      // 시간대 프로그램: 날짜 1개만 선택 (다시 누르면 해제)
      setSelectedDates(prev => (prev.includes(date) ? [] : [date]));
      setSelectedSlot(null);
      return;
    }
    setSelectedDates(prev =>
      prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]
    );
  };

  // 시간대 프로그램에서 선택된 날짜의 슬롯 상태
  const selectedDateSlots = useTimeSlots && selectedDates.length === 1
    ? slotsByDate[selectedDates[0]]
    : undefined;

  const handleMonthChange = (date: Date) => {
    setCurrentMonth(date);
    if (useTimeSlots) {
      // 시간대 프로그램은 하루만 선택 — 달을 넘기면 선택을 초기화해
      // 다른 달 날짜/슬롯이 남아 잘못 제출되는 것을 막는다
      setSelectedDates([]);
      setSelectedSlot(null);
    }
    // 다중선택 프로그램은 크로스먼스 선택을 위해 유지
  };

  const handleSubmitSuccess = (result: GroupBookingResult) => {
    setCompletedBooking(result);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!programId) {
    navigate('/application/group');
    return null;
  }

  return (
    <>
      <SEO
        title={`${program?.courseName || '단체체험'} 예약`}
        description="동두천시 글로벌인재교육센터 단체체험 예약 신청"
        path={`/application/group/${programId}`}
      />
      <div className="min-h-screen flex flex-col">
        <Header />

        {/* Page Header */}
        <div className="relative text-white py-12 sm:py-16 md:py-20 lg:py-24 mt-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1577896851231-70ef18881754?w=1920&h=600&fit=crop&q=80"
              alt="단체체험"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <p className="text-gray-200 mb-2 sm:mb-3 text-xs sm:text-sm font-medium tracking-wider">수강신청</p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4">
              {program?.courseName || '단체체험 예약'}
            </h1>
            <div className="w-16 sm:w-20 h-1 bg-white/70"></div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 py-8 sm:py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {completedBooking ? (
              <GroupBookingComplete
                bookingNumber={completedBooking.booking_number}
                bookingDates={completedBooking.booking_dates}
                timeSlot={completedBooking.time_slot}
                institutionName={completedBooking.institution_name}
                bookerName={completedBooking.booker_name}
              />
            ) : (
              <div className="space-y-8">
                {/* 안내 */}
                <div className="text-center">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">날짜를 선택해주세요</h2>
                  <p className="text-sm text-gray-500">
                    {useTimeSlots
                      ? '예약 가능한 날짜를 선택한 뒤 오전반 또는 오후반을 골라주세요.'
                      : '예약 가능한 날짜를 클릭하여 선택하세요. 다중 선택이 가능합니다.'}
                  </p>
                </div>

                {/* 캘린더 */}
                <GroupCalendar
                  currentMonth={currentMonth}
                  onMonthChange={handleMonthChange}
                  dateStatuses={dateStatuses}
                  selectedDates={selectedDates}
                  onDateToggle={handleDateToggle}
                  loading={loading}
                />

                {/* 다른 월에 선택된 날짜 요약 (시간대 미사용 프로그램만) */}
                {!useTimeSlots && selectedDates.length > 0 && (() => {
                  const currentYM = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
                  const otherMonthDates = selectedDates.filter(d => !d.startsWith(currentYM));
                  const currentMonthDates = selectedDates.filter(d => d.startsWith(currentYM));
                  if (otherMonthDates.length > 0) {
                    const formatD = (dateStr: string) => {
                      const d = new Date(dateStr + 'T00:00:00');
                      return `${d.getMonth() + 1}/${d.getDate()}`;
                    };
                    return (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800 font-medium">
                          총 {selectedDates.length}일 선택됨
                          {currentMonthDates.length > 0 && ` (이번 달 ${currentMonthDates.length}일)`}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          다른 달: {otherMonthDates.sort().map(formatD).join(', ')}
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* 신청 양식: 날짜 선택 시 노출. 시간대 프로그램은 폼 안에서 슬롯 선택 */}
                {selectedDates.length > 0 && (
                  <GroupBookingForm
                    programId={programId}
                    programName={program?.courseName || '단체체험'}
                    selectedDates={selectedDates}
                    timeSlot={useTimeSlots ? selectedSlot : null}
                    requireTimeSlot={useTimeSlots}
                    slotStatus={selectedDateSlots}
                    onTimeSlotChange={setSelectedSlot}
                    onSlotConflict={fetchDates}
                    onSubmitSuccess={handleSubmitSuccess}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default GroupBookingCalendar;
